/**
 * Shared utility: identifies system-level signal messages that should
 * NEVER be rendered as user-visible content or stored in chat history.
 *
 * Used by: storage.ts (before persisting), ChatList.tsx (preview),
 * ChatView.tsx (rendering), and any future consumer.
 */

/** All known system signal type prefixes */
const SYSTEM_PREFIXES = [
  '__aphenis_',
  '__paxel_',
  '__game_',
  '__contact_',
  '__deletion_',
  '__petworld_',
  '__key_verification',
  '__poll',
  '__time_capsule',
  '__mood_',
  '__ghost_thread',
  '__schedule_',
] as const;

const SYSTEM_EXACT_TYPES = new Set([
  '__aphenis_typing',
  '__aphenis_typing_stop',
  '__aphenis_delivered',
  '__aphenis_reaction',
  '__aphenis_edit',
  '__aphenis_delete',
  '__aphenis_read',
  '__aphenis_profile_update',
  '__aphenis_status_update',
  '__aphenis_status_reaction',
  '__aphenis_mood_update',
  '__aphenis_call_signal',
  '__paxel_profile',
  '__paxel_status',
  '__paxel_status_reaction',
  '__contact_removed',
  '__contact_blocked',
  '__contact_unblocked',
  '__contact_restored',
  '__deletion_request',
  '__petworld_invite',
  '__key_verification_request',
  '__key_verification_response',
]);

/**
 * Returns true if the message content is a system signal that should
 * never be shown to the user as a chat message or preview.
 */
export function isSystemSignal(content: string): boolean {
  if (!content) return false;

  // Quick heuristic: most system payloads start with '{'
  if (content.charAt(0) !== '{') return false;

  try {
    const parsed = JSON.parse(content);
    if (!parsed || typeof parsed !== 'object') return false;

    const type = parsed.type as string | undefined;
    if (!type) return false;

    // Check exact match
    if (SYSTEM_EXACT_TYPES.has(type)) return true;

    // Check prefix match
    for (const prefix of SYSTEM_PREFIXES) {
      if (type.startsWith(prefix)) return true;
    }

    return false;
  } catch {
    return false;
  }
}

/**
 * Returns a safe preview string for chat list cards.
 * If the content is a game invite/score, returns a friendly label.
 * If it's a system signal, returns empty string.
 * Otherwise returns the content truncated.
 */
const GAME_NAMES: Record<string, string> = {
  'tic-tac-toe': '🎯 Tic-Tac-Toe',
  'memory': '🧠 Memory Match',
  'snake': '🐍 Snake',
  'math': '🧮 Math Blitz',
  'tap-challenge': '👆 Tap Challenge',
  'reaction': '⚡ Reaction Test',
  'chess': '♟️ Chess',
};

export function getSafeMessagePreview(content: string, t?: (key: string) => string): string | null {
  if (!content) return null;

  if (content.charAt(0) === '{') {
    try {
      const p = JSON.parse(content);
      const type = p?.type as string | undefined;

      if (type === '__game_invite') {
        return `🎮 ${GAME_NAMES[p.gameType] || 'Game'} ${t?.('chat.gameInvite') || 'invite'}`;
      }
      if (type === '__game_score') {
        return `🏆 ${GAME_NAMES[p.gameType] || 'Game'} — ${p.label || t?.('chat.scoreShared') || 'Score'}`;
      }
      if (type === '__poll') {
        const q = typeof p.question === 'string' ? p.question : '';
        const trimmed = q.length > 30 ? q.slice(0, 30) + '…' : q;
        return trimmed ? `📊 ${t?.('chat.pollPreview') || 'Poll'}: ${trimmed}` : `📊 ${t?.('chat.pollPreview') || 'Poll'}`;
      }
      if (type === '__time_capsule') {
        const label = typeof p.sealedLabel === 'string' && p.sealedLabel ? p.sealedLabel : (t?.('chat.timeCapsulePreview') || 'Time capsule');
        return `🔒 ${label}`;
      }
      if (type === '__ghost_thread') {
        return `👻 ${t?.('chat.ghostThreadPreview') || 'Ghost thread'}`;
      }
      if (type === '__mood_update' || type?.startsWith('__mood_')) {
        const mood = typeof p.mood === 'string' ? p.mood : '';
        return mood ? `🎭 ${t?.('chat.moodPreview') || 'Mood'}: ${mood}` : `🎭 ${t?.('chat.moodPreview') || 'Mood update'}`;
      }
      if (type === '__key_verification_request' || type === '__key_verification_response') {
        return `🔑 ${t?.('chat.keyVerificationPreview') || 'Key verification'}`;
      }
      if (type?.startsWith('__schedule_')) {
        return `⏰ ${t?.('chat.scheduledPreview') || 'Scheduled message'}`;
      }
      if (type === '__petworld_invite') {
        return `🌍 ${t?.('chat.petWorldInvitePreview') || 'Pet World invite'}`;
      }
      // Any other valid JSON object → never show raw JSON to users
      if (isSystemSignal(content)) return null;
      // Even non-signal JSON must be suppressed — no raw JSON in previews
      return null;
    } catch {
      // Not JSON — passthrough
    }
  }

  return content.length > 40 ? content.slice(0, 40) + '…' : content;
}
